/**
 * Report templates: what a report looks like. A template has a report type —
 * the kind of object it reports on — and an ordered list of components, each
 * with its own parameters (heading, stat columns, row limit…). HTML templates
 * can also carry a logo, custom `<head>`/`<footer>` markup and a report
 * translation that relabels the stat columns.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'
import {
  REPORT_COMPONENTS,
  type ReportComponentParameters,
  type ReportFormat,
} from '@/services/reports/reportTypes'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface ReportRef {
  id: string
  name: string
}

/** One component placed on a template, in the order the report shows it. */
export interface ReportTemplateComponent {
  componentId: string
  parameters: ReportComponentParameters
}

export interface ReportTemplateDetail {
  id: string
  name: string
  description: string
  reportType: string
  reportFormat: ReportFormat
  /** Empty uses the network's report logo. */
  logo: string
  header: string
  footer: string
  /** Template-wide settings: column label overrides and the political ad filter. */
  parameters: Record<string, unknown>
  components: ReportTemplateComponent[]
  team: ReportRef | null
  reportTranslation: ReportRef | null
  objectState: string
  createTime: string
  updateTime: string
  validationWarnings: ValidationWarning[]
}

export interface ReportTemplateInput {
  name: string
  description?: string
  reportType?: string
  reportFormat?: ReportFormat
  logo?: string
  header?: string
  footer?: string
  parameters?: Record<string, unknown>
  components?: { reportComponent: { id: string }; parameters: ReportComponentParameters }[]
  /** `null` takes the template off its team. */
  team?: { id: string } | null
  /** `null` goes back to the default English labels. */
  reportTranslation?: { id: string } | null
  objectState?: string
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

function mapRef(value: unknown): ReportRef | null {
  const ref = value as Record<string, unknown> | null | undefined
  return ref?.id ? { id: str(ref.id), name: str(ref.name) } : null
}

/**
 * The report engine hands parameters back as strings, so booleans and numbers
 * the editor deals in are read back into their own types. A component's own
 * defaults fill in anything an older template never stored.
 */
function mapComponent(raw: Record<string, unknown>): ReportTemplateComponent {
  const ref = raw.reportComponent
  const componentId = typeof ref === 'string' ? ref : str((ref as Record<string, unknown>)?.id)
  const stored = (raw.parameters as Record<string, unknown>) ?? {}
  const defaults = REPORT_COMPONENTS[componentId]?.parameters ?? {}
  const parameters: ReportComponentParameters = { ...defaults, ...coerce(stored) }
  if (defaults.fields) {
    parameters.fields = { ...defaults.fields, ...coerceFields(stored.fields) }
  }
  if (!parameters.heading && defaults.heading) parameters.heading = defaults.heading
  return { componentId, parameters }
}

function coerce(params: Record<string, unknown>): ReportComponentParameters {
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => {
      if (value === 'true') return [key, true]
      if (value === 'false') return [key, false]
      if ((key === 'limit' || key === 'height') && typeof value === 'string' && value !== '') {
        return [key, Number(value)]
      }
      return [key, value]
    })
  )
}

function coerceFields(value: unknown): Record<string, boolean> {
  if (!value || typeof value !== 'object') return {}
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, v === true || v === 'true'])
  )
}

function mapTemplate(r: Record<string, unknown>): ReportTemplateDetail {
  return {
    id: str(r.id),
    name: str(r.name),
    description: str(r.description),
    reportType: str(r.reportType),
    reportFormat: (str(r.reportFormat) || 'HTML') as ReportFormat,
    logo: str(r.logo),
    header: str(r.header),
    footer: str(r.footer),
    parameters: (r.parameters as Record<string, unknown>) ?? {},
    components: toArray<Record<string, unknown>>(r.components ?? []).map(mapComponent),
    team: mapRef(r.team),
    reportTranslation: mapRef(r.reportTranslation),
    objectState: str(r.objectState),
    createTime: str(r.createTime),
    updateTime: str(r.updateTime),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
  }
}

export interface ReportTemplateListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

async function save(id: string, input: ReportTemplateInput, fallback: string) {
  const res = await apiFetch(`${API_BASE}/reporttemplates/${id}?context=${getContext()}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  })
  if (!res.ok) await throwApiError(res, fallback)
  return mapTemplate(await res.json())
}

export const ReportTemplatesApi = {
  getPage: async ({
    page = 1,
    pageSize = 25,
    search,
    status,
    sort,
  }: ReportTemplateListOptions = {}): Promise<ListPage<ReportTemplateDetail>> => {
    const params = new URLSearchParams({
      ...searchParams('reportTemplates', search),
      context: getContext(),
      includeHidden: 'false',
      ...statusParams(status),
      ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
      page: String(page),
      pageSize: String(pageSize),
    })
    const res = await apiFetch(`${API_BASE}/reporttemplates?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch report templates')
    const data = (await res.json()) as { totalCount?: number }
    const results = toArray<Record<string, unknown>>(data).map(mapTemplate)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  /** Every active template, for pickers. */
  getAll: async (): Promise<ReportTemplateDetail[]> =>
    (await ReportTemplatesApi.getPage({ pageSize: 100, status: 'ACTIVE' })).results,

  getById: async (id: string): Promise<ReportTemplateDetail> => {
    const res = await apiFetch(`${API_BASE}/reporttemplates/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch report template')
    return mapTemplate(await res.json())
  },

  create: (id: string, input: ReportTemplateInput) =>
    save(id, input, 'Failed to create report template'),

  update: (id: string, input: ReportTemplateInput) =>
    save(id, input, 'Failed to save report template'),

  /** A new template with another's settings and components under a new name. */
  copy: async (
    source: ReportTemplateDetail,
    newId: string,
    name: string,
    description: string
  ): Promise<ReportTemplateDetail> =>
    save(
      newId,
      {
        name,
        description,
        reportType: source.reportType,
        reportFormat: source.reportFormat,
        logo: source.logo,
        header: source.header,
        footer: source.footer,
        parameters: source.parameters,
        components: toComponentInput(source.components),
        team: source.team ? { id: source.team.id } : null,
        reportTranslation: source.reportTranslation ? { id: source.reportTranslation.id } : null,
        objectState: 'ACTIVE',
      },
      'Failed to copy report template'
    ),
}

/** Components as the API takes them, in the order they are listed. */
export function toComponentInput(components: ReportTemplateComponent[]) {
  return components.map((c) => ({ reportComponent: { id: c.componentId }, parameters: c.parameters }))
}

/* ── Column labels ───────────────────────────────────────────────────── */

/** Every template parameter that relabels a column starts with this. */
export const TRANSLATION_PREFIX = 'translation'

/** English labels each component falls back to, unless the template relabels them. */
const COMPONENT_LABELS: Record<string, { field: string; label: string }> = {
  translationImpressions: { field: 'impressions', label: 'Impressions' },
  translationRendered: { field: 'rendered', label: 'Rendered Impressions' },
  translationRenderedRate: { field: 'renderedRate', label: 'Rendered Rate' },
  translationEarnings: { field: 'earnings', label: 'Earnings' },
  translationRevenueShare: { field: 'revenueShare', label: 'Revenue Share' },
}

const OVERRIDE_LABELS: Record<string, string> = { cost: 'Cost' }

/**
 * Components as they are saved. With a report translation chosen, the
 * translation does all the labelling and any label kept on a component would
 * fight it, so they are dropped. Otherwise each component is told its column
 * labels — "Cost" rather than "Earnings" on the advertiser side, as the old UI
 * always labelled it, and elsewhere the template's own wording where it has
 * some.
 */
export function prepareComponents(
  components: ReportTemplateComponent[],
  templateParameters: Record<string, unknown>,
  hasReportTranslation: boolean
): ReportTemplateComponent[] {
  return components.map(({ componentId, parameters }) => {
    const kept = Object.fromEntries(
      Object.entries(parameters).filter(([key]) => !key.startsWith(TRANSLATION_PREFIX))
    ) as ReportComponentParameters
    if (hasReportTranslation) return { componentId, parameters: kept }
    const overrides = REPORT_COMPONENTS[componentId]?.translationOverrides ?? {}
    const labels = Object.fromEntries(
      Object.entries(COMPONENT_LABELS).map(([key, { field, label }]) => {
        const override = overrides[field]
        if (override) return [key, OVERRIDE_LABELS[override] ?? label]
        const own = templateParameters[key]
        return [key, typeof own === 'string' && own.trim() ? own : label]
      })
    )
    return { componentId, parameters: { ...kept, ...labels } }
  })
}
